/**
 * Application Constants
 * Centralized constants for the application
 */

// ============================================================================
// API Configuration
// ============================================================================

export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const

export const AUTH_CONFIG = {
  BASE_URL: process.env.REACT_APP_AUTH_API_BASE_URL,
  LOGIN_ENDPOINT: '/login',
  // Exchanges the stored refresh token for a fresh access token. Derived from the same
  // BASE_URL as login so the two can never drift onto different hosts.
  REFRESH_ENDPOINT: '/refresh',
  TIMEOUT: 30000,
} as const

// ============================================================================
// API Endpoints
// ============================================================================

export const API_ENDPOINTS = {
  // Articles
  ARTICLES: '/api/articles',
  ARTICLES_BY_ID: (id: string) => `/api/articles/${id}`,
  ARTICLES_BY_CATEGORY: (slug: string) => `/api/articles/category/${slug}`,
  
  // Categories
  CATEGORIES: '/api/categories',
  CATEGORIES_BY_ID: (id: string) => `/api/categories/${id}`,
  
  // Subcategories
  SUBCATEGORIES: '/api/subcategories',
  SUBCATEGORIES_BY_ID: (id: string) => `/api/subcategories/${id}`,
  SUBCATEGORIES_BY_CATEGORY: (categoryId: string) => `/api/subcategories/by-category/${categoryId}`,
  
  // Writer Tags
  WRITERS: '/api/writers',
  WRITERS_BY_ID: (id: string) => `/api/writers/${id}`,
  
  // Events
  EVENTS: '/api/events-bo',
  EVENTS_BY_ID: (id: string) => `/api/events-bo/${id}`,
  EVENTS_FEATURED: '/api/events-bo/featured',
  
  // Daily Living
  DAILY_LIVING_SECTIONS: '/api/daily-living-sections',
  DAILY_LIVING_SECTIONS_BY_ID: (id: string) => `/api/daily-living-sections/${id}`,
  
  // Content Slots
  CONTENT_SLOTS: '/api/content-slots',
  CONTENT_SLOTS_BY_SECTION: (sectionId: string) => `/api/content-slots/section/${sectionId}`,
  
  // Upload
  UPLOAD: '/api/upload',

  // AI / SEO
  AI_SEO_GENERATE: '/api/ai/seo/generate',
  AI_SEO_TRANSLATE_CONTENT: '/api/ai/seo/translate-content',
  AI_SEO_TRANSLATE_LABEL: '/api/ai/seo/translate-label',
  AI_SEO_TRANSLATE_FIELDS: '/api/ai/seo/translate-fields',
  AI_SEO_SUGGEST_TAGS: '/api/ai/seo/suggest-tags',
  AI_GUIDES_RESTRUCTURE: '/api/ai/guides/restructure',
  AI_GUIDES_RELATED: '/api/ai/guides/related',
  AI_GUIDES_QUESTIONS: '/api/ai/guides/questions',
} as const

// ============================================================================
// Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'token',
  // Long-lived token exchanged at AUTH_CONFIG.REFRESH_ENDPOINT for a new AUTH_TOKEN.
  // Written at login, cleared at logout. Without it the session cannot be kept alive
  // and the admin is logged out the moment the access token expires.
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'userData',
  THEME: 'theme',
  LANGUAGE: 'language',
} as const

// ============================================================================
// Route Paths
// ============================================================================

export const ROUTE_PATHS = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  LOGIN: '/login',
  LOGOUT: '/logout',
  
  // Articles
  ARTICLES: '/articles',
  ARTICLES_CREATE: '/articles/create',
  ARTICLES_EDIT: '/articles/edit/:id',
  
  // Categories
  CATEGORIES: '/categories',
  CATEGORIES_CREATE: '/categories/create',
  CATEGORIES_EDIT: '/categories/edit/:id',
  
  // Subcategories
  SUBCATEGORIES: '/subcategories',
  SUBCATEGORIES_CREATE: '/subcategories/create',
  SUBCATEGORIES_EDIT: '/subcategories/edit/:id',
  
  // Writer Tags
  WRITER_TAGS: '/writer-tags',
  WRITER_TAGS_CREATE: '/writer-tags/create',
  WRITER_TAGS_EDIT: '/writer-tags/edit/:id',
  
  // Events
  EVENTS: '/events',
  EVENTS_CREATE: '/events/create',
  EVENTS_EDIT: '/events/edit/:id',
  
  // Daily Living
  DAILY_LIVING: '/dailyliving',
  DAILY_LIVING_MAIN: '/dailyliving/main',
  DAILY_LIVING_TOPICS: '/dailyliving/topics',
  DAILY_LIVING_SECTIONS: '/manage/daily-living-sections',
} as const

// ============================================================================
// UI Constants
// ============================================================================

export const UI_CONSTANTS = {
  // Pagination
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  
  // Slots
  MAX_FEATURED_EVENTS_SLOTS: 6,
  MAX_TOP_SECTION_SLOTS: 3,
  MAX_DAILY_LIVING_SLOTS: 6,
  
  // File Upload
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  
  // Delays
  DEBOUNCE_DELAY: 300,
  TOAST_DURATION: 5000,
  
  // Text Limits
  MAX_TITLE_LENGTH: 500,
  MAX_SLUG_LENGTH: 500,
  MAX_META_DESCRIPTION_LENGTH: 1000,
  MAX_META_KEYWORDS_LENGTH: 255,
} as const

// ============================================================================
// Status Options
// ============================================================================

export const STATUS_OPTIONS = [
  { value: 'unpublished', label: 'Unpublished' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
] as const

export const CATEGORY_TYPE_OPTIONS = [
  { value: 'article', label: 'Article' },
  { value: 'event', label: 'Event' },
  { value: 'community', label: 'Community' },
] as const

export const ACCESS_TYPE_OPTIONS = [
  { value: 'free_access', label: 'Free Access' },
  { value: 'open_registration', label: 'Open Registration' },
  { value: 'fees', label: 'Fees' },
] as const

export const TIME_TYPE_OPTIONS = [
  { value: 'general_time', label: 'General Time' },
  { value: 'specific_time', label: 'Per Day Time' },
] as const

// ============================================================================
// Qatar Zones
// ============================================================================

export const QATAR_ZONES = [
  'Doha',
  'Al Rayyan',
  'Al Wakrah',
  'Al Khor',
  'Umm Salal',
  'Al Shamal',
  'Al Daayen',
  'Mesaieed',
] as const

// ============================================================================
// Days of Week
// ============================================================================

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
} as const

// ============================================================================
// Success Messages
// ============================================================================

export const SUCCESS_MESSAGES = {
  CREATED: 'Successfully created',
  UPDATED: 'Successfully updated',
  DELETED: 'Successfully deleted',
  SAVED: 'Successfully saved',
  PUBLISHED: 'Successfully published',
} as const

// ============================================================================
// Validation Rules
// ============================================================================

export const VALIDATION_RULES = {
  REQUIRED: 'This field is required',
  EMAIL: 'Please enter a valid email address',
  URL: 'Please enter a valid URL',
  MIN_LENGTH: (min: number) => `Minimum length is ${min} characters`,
  MAX_LENGTH: (max: number) => `Maximum length is ${max} characters`,
  POSITIVE_NUMBER: 'Please enter a positive number',
} as const

// ============================================================================
// Theme
// ============================================================================

export const THEME_COLORS = {
  PRIMARY: '#ff7849',
  PRIMARY_HOVER: '#ff6030',
  SECONDARY: '#6c757d',
  SUCCESS: '#28a745',
  DANGER: '#dc3545',
  WARNING: '#ffc107',
  INFO: '#17a2b8',
  LIGHT: '#f8f9fa',
  DARK: '#343a40',
} as const

